"use client";

// KAN-283 (Часть 2): полноэкранный оверлей «сайт обновляется». Показывается, когда
// backend недоступен (см. maintenance-store), и авто-скрывается по возврату /health.
// Только design-tokens, next-intl namespace "Maintenance".
import { useEffect, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { stripLocale } from "@/i18n/routing";

import {
  getMaintenanceServerSnapshot,
  getMaintenanceSnapshot,
  subscribeMaintenance,
} from "@/shared/lib/maintenance-store";
import { isSlowNetwork, useOnline } from "@/shared/lib/network";

// KAN-1609: сервер недоступен дольше 15 с — возможно, дело не в обновлении, а в сети пользователя: подсказываем
// про слабую сеть или VPN. Сети нет вовсе — оверлей не нужен: полосу «нет подключения» показывает NetworkHintBar.
const HINT_AFTER_MS = 15_000;

export function MaintenanceOverlay() {
  const pathname = stripLocale(usePathname() ?? "/");
  const isDown = useSyncExternalStore(
    subscribeMaintenance,
    getMaintenanceSnapshot,
    getMaintenanceServerSnapshot,
  );
  const t = useTranslations("Maintenance");
  const online = useOnline();
  const [longDown, setLongDown] = useState(false);
  useEffect(() => {
    if (!isDown) return;
    const timer = window.setTimeout(() => setLongDown(true), HINT_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
      setLongDown(false);
    };
  }, [isDown]);

  // Чат показывает свою плашку связи и сохраняет доступ к черновикам и повторам отправки.
  if (!isDown || !online || pathname.startsWith("/chat") || pathname.startsWith("/messages")) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-2 border-muted border-b-primary" />
        <h2 className="mb-2 text-lg font-semibold text-foreground">
          {t("title")}
        </h2>
        <p className="mb-1 text-sm text-muted-foreground">{t("description")}</p>
        <p className="text-xs text-muted-foreground/80">{t("retrying")}</p>
        {longDown && (
          <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-foreground">
            {t(isSlowNetwork() ? "slowHint" : "vpnHint")}
          </p>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {t("reload")}
        </button>
      </div>
    </div>
  );
}
